<template>
	<div :class="'egranty-table' + (compact ? ' compact-table' : '')">
		<v-toolbar class="egrantyTableToolbar" flat>
			<v-toolbar-title>{{ tableName }}</v-toolbar-title>
			<v-spacer></v-spacer>

			<v-btn color="primary" class="ml-3" @click="exportData" dark>
				<v-icon class="export-icon">save_alt</v-icon>
				Eksportuj
			</v-btn>

			<hideable-columns-menu :columns="headers"
				@column-toggled="columnToggled"
				:column-visibilities="columnVisibility"
				v-if="hideableColumns"/>

		</v-toolbar>

		<div>
			<v-btn class="mb-2 ml-3" v-if="toolbarActionName !== '' && toolbarActionVisible"
				color="primary" dark
				@click="$emit('toolbarAction')">
				{{ toolbarActionName }}
			</v-btn>
		</div>

		<v-data-table
			:headers="computedHeaders"
			:items="computedItems"
			:hide-actions="hidePagination"
			item-key="name"
			class="elevation-1 ranking-list-table"
		>

			<template slot="headerCell" slot-scope="{ header }">
				<div :class="'column-header ' + getHeaderClass(header)">
					<template v-if="header.filterable">
						<v-text-field
							browser-autocomplete="off"
							v-model="searchModel[header.value]"
							append-icon="search"
							hide-details box clearable
							class="pa-0 mb-2 egranty-table-header-search"
							@click="eatEvent"
							v-if="header.type === 'TEXT' || header.type === 'NUMBER' || header.type === 'CURRENCY'"
							aria-label="label for text"
						></v-text-field>
						<egranty-three-state-switch
							v-model="searchModel[header.value]"
							@click="eatEvent"
							v-if="header.type === 'BOOLEAN'"
						></egranty-three-state-switch>
					</template>

					<v-tooltip bottom v-if="header.tooltip">
						<template v-slot:activator="{ on }">
							<span v-text="header.text" v-on="on" class="mr-3"/>
						</template>
						<span>{{header.tooltip}}</span>
					</v-tooltip>
					<span v-else v-text="header.text" class="mr-3"/>
				</div>
			</template>

			<template slot="items" slot-scope="{ item, index }">
				<tr>
					<template v-for="(header, headerIndex) in computedHeaders">
						<template v-if="header.editable">
							<td class="text-xs-center" :key="'cell-' + headerIndex">
								<v-text-field v-model="items[item.egrantyTableIndex][header.value]"
									browser-autocomplete="off"
									@change="notifyItemUpdated(items[item.egrantyTableIndex], header.value)"
									v-if="header.type === 'TEXT'"
									aria-label="label for text">
								</v-text-field>
								<v-text-field v-model="items[item.egrantyTableIndex][header.value]"
									browser-autocomplete="off"
									@change="notifyItemUpdated(items[item.egrantyTableIndex], header.value)"
									mask="##################"
									v-if="header.type === 'NUMBER' || header.type === 'CURRENCY'"
									aria-label="label for text">
								</v-text-field>
								<egranty-three-state-switch v-model="items[item.egrantyTableIndex][header.value]"
									@change="notifyItemUpdated(items[item.egrantyTableIndex], header.value)"
									v-if="header.type === 'BOOLEAN'">
								</egranty-three-state-switch>
							</td>
						</template>

						<td :class="getCellClass(header)" :key="'cell-' + headerIndex"
							v-else-if="!header.isActionsHeader && !header.editable">
							<template v-if="header.type === 'BOOLEAN'">
								<template v-if="item[header.value] === true">
									<v-icon>done</v-icon>
								</template>
								<template v-else-if="item[header.value] === false">
									<v-icon>clear</v-icon>
								</template>
								<template v-else>
									<v-icon v-if="agencyEvaluation(header.value)">{{ item.agencyEvaluationAssigned ?
										'help_outline' : '' }}
									</v-icon>
									<v-icon v-else-if="expertEvaluation(header.value)">{{ expertEvaluatorPresent(item,
										header.value) ? 'help_outline' : '' }}
									</v-icon>
									<v-icon v-else>help_outline</v-icon>
								</template>
							</template>
							<template v-else-if="header.type === 'CURRENCY'">
								{{ formatMoney(item[header.value]) }}
							</template>
							<template v-else-if="header.type === 'NUMBER' && header.value === 'index'">{{index+1}}</template>
							<template v-else-if="header.type === 'COMMITTEE_DECISION'">
								{{committeeDecisions[item[header.value]]}}
							</template>
							<template v-else-if="header.type === 'NUMBER'">
								{{ formatWithoutFixed(item[header.value]) }}
							</template>
							<template v-else-if="substantiveEvaluation(header.value)">
								<template v-if="substantiveEvaluatorPresent(item, header.value)">
									<template v-if="item[header.value]">{{ item[header.value].toString().replace(/\./g,
										",") }}
									</template>
									<template v-else>
										<v-icon>help_outline</v-icon>
									</template>
								</template>
								<template v-else>
									{{ item[header.value] ? item[header.value].toString().replace(/\./g, ",") :
										item[header.value] }}
								</template>
							</template>
							<template v-else>
								{{ item[header.value] && !isNaN(item[header.value]) ?
									item[header.value].toString().replace(/\./g, ",") :
									item[header.value] }}
							</template>
						</td>
					</template>

					<td v-if="hasActions && rowActionsVisible" class="text-xs-center">
						<v-layout row>
							<template v-for="(action, actionIndex) in rowActions">
								<egranty-icon-button
									small
									:key="'action-' + actionIndex"
									:icon="action.icon"
									:label="action.label"
									@click="$emit(action.name, item)"
									v-if="getVisibilityFunction(action)(item)"
									:disabled="getDisabledFunction(action)(item)"
									:color="getIconColor(action)"
								></egranty-icon-button>
							</template>
						</v-layout>
					</td>
				</tr>
				<tr v-if="hasFooterWithSums && index === computedItems.length - 1" class="footer-row">
					<template v-for="(header, headerIndex) in computedHeaders">
						<td :class="getCellClass(header) + ' footer-cell'" :key="'footer-cell-' + headerIndex">
							{{ header.summable ? formatMoney(header.computedSum) : "" }}
						</td>
					</template>
				</tr>
			</template>
		</v-data-table>
	</div>
</template>

<script>
	import {Files} from "../../../utils/Files";
	import {Dates} from "../../../utils/Dates";
	import {Numbers} from "../../../utils/Numbers";
	import {Api} from "../../../api";
	import EgrantyIconButton from "../../../genericComponents/EgrantyIconButton";
	import EgrantyThreeStateSwitch from "../../../genericComponents/EgrantyThreeStateSwitch";
	import HideableColumnsMenu from "../../../genericComponents/egrantyTable/HideableColumnsMenu";

	export default {

		name: "RankingListTable",
		components: {HideableColumnsMenu, EgrantyThreeStateSwitch, EgrantyIconButton},
		mixins: [Files, Dates, Numbers],


		data() {
			return {
				columnVisibility: null,
				searchModel: {},
				committeeDecisions: {'': '-',
					'RECOMMENDED' : 'TAK',
					'NOT_RECOMMENDED': 'NIE',
					'RESERVE': 'REZERWA'},
			};
		},

		watch: {
			headers: function (newVal) {
				if (this.columnVisibility === null) {
					this.columnVisibility = newVal
						.reduce((obj, h) => {
							obj[h.value] = true;
							return obj;
						}, {});
				}

				this.searchModel = newVal
					.reduce((obj, header) => {
						if (header.type === "BOOLEAN") {
							obj[header.value] = null;
						} else {
							obj[header.value] = "";
						}
						return obj;
					}, {});
			},

			items: function () {
				this.items.forEach(offer => {
					let decision = {
						id: offer.id,
						value: offer.committeeDecision === 'RESERVE' ? 'REZERWA' :
							offer.committeeDecision === 'RECOMMENDED' ? 'TAK' :
							offer.committeeDecision === 'NOT_RECOMMENDED' ? 'NIE' : '-'
					};
					this.decisionLinkers.push(decision);
				});
			}
		},

		computed: {
			columnFilters() {
				return this.headers.map(
					header => {
						let ctx = this;
						switch (header.type) {
							case "NUMBER":
								return getNumberFilter(ctx, header.value);
							case "CURRENCY":
								return getNumberFilter(ctx, header.value);
							case "TEXT":
								return getTextFilter(ctx, header.value);
							case "BOOLEAN":
								return getBooleanFilter(ctx, header.value);
							default:
								return getTextFilter(ctx, header.value);
						}
					},
				);
			},

			computedHeaders() {
				let computedHeaders = [...this.headers];

				if (this.hasActions) {
					computedHeaders.push({
						text: "Akcje",
						width: "10%",
						value: "egranty-table-actions",
						align: "center",
						sortable: false,
						isActionsHeader: true,
					});
				}

				if (this.hideableColumns) {
					computedHeaders = computedHeaders.filter(h => this.columnVisibility[h.value]);
				}

				computedHeaders
					.filter(h => !h.type && !h.isActionsHeader)
					.forEach(h => this.$set(h, "type", "TEXT"));

				return computedHeaders;
			},

			computedItems() {
				let result = [...this.items];
				result = JSON.parse(JSON.stringify(result));	// deepcopy
				for (let i = 0; i < result.length; ++i) {
					this.$set(result[i], "egrantyTableIndex", i);
				}
				//		let allFilters = [...this.rowFilters, ...this.columnFilters];
				//		allFilters.forEach(f => result = result.filter(f));
				this.computedHeaders.forEach(
					header => {
						this.$set(header, "computedSum", 0);
						result.forEach(item => {
							if (header.postProcessor) {
								this.$set(item, header.value, header.postProcessor(item));
							}
							if (header.summable) {
								header.computedSum = header.computedSum + (parseInt(item[header.value]) || 0);
							}
						});
					},
				);

				return result;
			},

			hasActions() {
				return this.rowActions.length > 0;
			},

			hasFooterWithSums() {
				return this.headers.some(h => h.summable);
			},
		},

		created () {
			this.decisionLinkers = [];
		},

		methods: {
			eatEvent(e) {
				e.stopPropagation();
			},

			getVisibilityFunction(action) {
				return typeof action.isVisible === "function" ? action.isVisible : () => true;
			},

			getDisabledFunction(action) {
				return typeof action.isDisabled === "function" ? action.isDisabled : () => false;
			},

			getIconColor(action) {
				return action.icon === 'delete' ? 'lightred' : 'primary';
			},

			notifyItemUpdated(item, key) {
				this.$emit("item-updated", item, key);
			},

			columnToggled(columnKey, newVisibility) {
				this.columnVisibility[columnKey] = newVisibility;
			},

			getHeaderClass(header) {
				let cssClass = "";
				switch (header.type) {
					case "TEXT":
						cssClass = "text-column-header";
						break;
					case "NUMBER":
						cssClass = "number-column-header";
						break;
					case "CURRENCY":
						cssClass = "number-column-header";
						break;
					case "BOOLEAN":
						cssClass = "boolean-column-header";
						break;
				}
				if (header.headerClass) {
					cssClass += " " + header.headerClass;
				}
				return cssClass;
			},

			getCellClass(header) {
				let cssClass = "";
				switch (header.type) {
					case "TEXT":
						return "text-xs-left";
					case "NUMBER":
						return "text-xs-right";
					case "CURRENCY":
						return "text-xs-right";
					case "BOOLEAN":
						return "text-xs-center";
				}
				if (header.cellClass) {
					cssClass += " " + header.cellClass;
				}
				return cssClass;
			},

			exportData() {
				let data = {
					headers: this.computedHeaders
						.filter(h => !h.isActionsHeader)
						.map(h => {
							return {
								caption: h.text,
								key: h.value,
								type: h.type,
							};
						}, {}),
					rows: this.computedItems,
				};
				Api.post(this, "/data-export/xls", data,
					success => {
						let filename = "egranty_export_" + this.getFilenameTimestamp() + ".xlsx";
						this.$messagesBus.$emit("success", "Eksport danych z tabeli wykonano do pliku " + filename,
							success);
						this.downloadBlobResponse(success, filename);
					}, error => {
						this.$messagesBus.$emit("error", "Eksport nieudany", error);
					},
					{responseType: "blob"});
			},

			substantiveEvaluation (headerValue) {
				return headerValue.startsWith('substantiveEvaluationsPointSums');
			},

			agencyEvaluation (headerValue) {
				return headerValue.startsWith('agencyEvaluation');
			},

			expertEvaluation (headerValue) {
				return headerValue.startsWith('expertEvaluations');
			},

			substantiveEvaluatorPresent (item, headerValue) {
				let evaluationIndex = headerValue.substring(31);
				let found = this.computedItems.find(computed => computed.id === item.id);
				return found['committeeNames' + evaluationIndex];
			},

			expertEvaluatorPresent (item, headerValue) {
				let evaluationIndex = headerValue.substring(17);
				return evaluationIndex <= item.expertEvaluationCount;
			},

			removeEmptyTableLoading () {
				var el = document.querySelector('.v-datatable__progress');
				el.parentNode.removeChild(el);
			}
		},


		mounted() {
			this.removeEmptyTableLoading();
		},


		props: {
			/**
			 * Kopiowane, filtrowane i przekazywane do v-data-table:items.
			 */
			items: {type: Array, required: true, default: () => []},

			/**
			 * Kopiowane i przekazywane do v-data-table:headers. Jeśli tabela zawiera akcje,
			 * dodawany jest nagłówek z akcjami.
			 *
			 * Dodatkowe pola:
			 * - postProcessor: (Function(item)) - funkcja do zmiany wartości danego pola dla item
			 * - filterable: (Boolean) - czy kolumna może być filtrowana
			 * - editable: (Boolean) - czy pola można edytować
			 * - summable: (Boolean) - czy kolumna będzie zawierała sumę z komórek w ostatnim wierszu tabeli
			 * - type: (String: TEXT, NUMBER, CURRENCY, BOOLEAN) - wpływa na metodę filtrowania i sortowania
			 * - cellClass: (String) - klasa css dodawana do każdej komórki
			 * - headerClass: (String) - klasa css dodawana do każdego nagłówka
			 */
			headers: {type: Array, required: true, default: () => []},
			rowActions: {type: Array, default: () => []},
			rowActionsVisible: {type: Boolean, default: true},
			toolbarActionName: {type: String, default: ""},
			toolbarActionVisible: {type: Boolean, default: true},
			hidePagination: {type: Boolean, default: false},
			tableName: {type: String, default: ""},
			rowFilters: {type: Array, default: () => []},
			hideableColumns: {type: Boolean, default: false},
			exportEnabled: {type: Boolean, default: false},
			compact: {type: Boolean, default: false},
		},
	};

	function getTextFilter(ctx, key) {
		return getFilter(ctx, key, (v, q) => {
			if (v && q) {
				return v.toUpperCase().indexOf(q.toUpperCase()) >= 0;
			} else {
				return null;
			}
		});
	}

	function getNumberFilter(ctx, key) {
		return getFilter(ctx, key, (v, q) => v.toString().startsWith(q));
	}

	function getBooleanFilter(ctx, key) {
		return getFilter(ctx, key, (v, q) => v === q);
	}

	function getFilter(ctx, key, testFunction) {
		return function (item) {
			if (ctx.searchModel[key] === "" || ctx.searchModel[key] === null || ctx.searchModel[key] === undefined) {
				return true;
			}

			if (!item[key] && ctx.searchModel[key]) {
				return false;
			}

			return testFunction(item[key], ctx.searchModel[key]);
		};
	}
</script>

<style>
	.ranking-list-table {
		min-width: 100%;
	}

	.ranking-list-table table {
		border: 1px solid #ddd;
	}
	.v-text-field--box > .v-input__control > .v-input__slot {
		height: 24px;
		line-height: 24px;
		min-height: 24px;
	}

	.v-text-field--box > .v-input__control > .v-input__slot input {
		padding: 0px;
		margin: 0px;
		font-size: 0.7em;
	}

	.v-text-field.v-text-field--enclosed .v-input__append-inner {
		margin-top: 0px;
	}

	.v-text-field.v-text-field--enclosed > .v-input__control > .v-input__slot {
		padding: 0 5px;
	}

	.v-text-field .v-input__append-inner {
		padding-left: 0px;
	}

	.v-datatable thead th.column.sortable.active.desc .v-input .v-icon {
		-webkit-transform: none;
		transform: none;
	}

	.v-datatable thead th.column.sortable .boolean-column-header button i.v-icon {
		opacity: 1;
		color: inherit;
	}

	.boolean-column-header button {
		display: block;
		margin-left: auto;
		height: 26px;
		width: 26px;
		margin-right: auto;
		margin-top: 0;
	}

	tr.footer-row {
		background: lightgray;
		font-weight: bold;
	}

	.compact-table .v-text-field.v-text-field--enclosed .v-input__append-inner {
		height: 18px;
		width: 18px;
		min-width: 18px;
	}

	.compact-table table.v-table tbody td {
		font-weight: 400;
		font-size: 14px;
		padding: 0 4px;
	}

	.compact-table table.v-table thead th {
		font-weight: 400;
		font-size: 10px;
		padding: 0 4px;
		vertical-align: top;
	}

	.compact-table table.v-table tbody td input {
		font-weight: 400;
		font-size: 10px;
	}

	.compact-table .column-header span {
		display: inline-block;
		white-space: normal;
	}

	.compact-table .text-column-header span {
		min-width: 130px;
	}

	.compact-table .header-offerCode {
		max-width: 130px;
	}

	.compact-table .number-column-header span {
		min-width: 42px;
	}

	.compact-table .boolean-column-header span {
		min-width: 42px;
	}

	.compact-table .column-header.header-index span {
		min-width: auto;
	}

	.theme--dark .v-toolbar {
		background-color: #1B1B1B !important;
	}
	.ranking-list-table tbody td:not(:last-of-type) {
		border-right: 1px solid #ddd;
	}

</style>
